import React, { useEffect, useState } from "react";


const STATUS_COLORS = {
  unread:  { bg: "#000",    text: "#fff" },
  read:    { bg: "#e0e0e0", text: "#000" },
  replied: { bg: "#f8f8f8", text: "#888" },
};

export default function AdminMessages() {
  const [messages, setMessages]         = useState([]);
  const [filter, setFilter]             = useState("all");
  const [expanded, setExpanded]         = useState(null);
  const [replyText, setReplyText]       = useState("");
  const [loading, setLoading]           = useState(true);
  const [replyLoading, setReplyLoading] = useState(false);
  const [toast, setToast]               = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const q = filter !== "all" ? `?status=${filter}` : "";
      const res = await fetch(`/api/contact/messages${q}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
      const data = await res.json();
      setMessages(data);
    } catch {
      showToast("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, [filter]);

  const markRead = async (id) => {
    await fetch(`/api/contact/messages/${id}/read`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
    });
    setMessages((prev) =>
      prev.map((m) => (m._id === id ? { ...m, status: "read" } : m))
    );
  };

  const handleExpand = (msg) => {
    setExpanded(expanded?._id === msg._id ? null : msg);
    setReplyText("");
    if (msg.status === "unread") markRead(msg._id);
  };

  const handleReply = async (id) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      const res = await fetch(`/api/contact/messages/${id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({ replyText }),
      });
      if (!res.ok) throw new Error();
      setMessages((prev) =>
        prev.map((m) =>
          m._id === id ? { ...m, status: "replied", adminReply: replyText } : m
        )
      );
      setReplyText("");
      setExpanded((prev) => ({ ...prev, status: "replied", adminReply: replyText }));
      showToast("Reply sent successfully.");
    } catch {
      showToast("Failed to send reply.");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await fetch(`/api/contact/messages/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
    });
    setMessages((prev) => prev.filter((m) => m._id !== id));
    if (expanded?._id === id) setExpanded(null);
    showToast("Message deleted.");
  };

  const counts = {
    all:     messages.length,
    unread:  messages.filter((m) => m.status === "unread").length,
    read:    messages.filter((m) => m.status === "read").length,
    replied: messages.filter((m) => m.status === "replied").length,
  };

  return (
    <>
      <style>{adminStyles}</style>

      <div className="am-root">

        {/* ── HEADER ── */}
        <div className="am-header">
          <div>
            <span className="am-eyebrow">Admin Panel</span>
            <h1 className="am-title">
              Contact <span className="am-title-bold">Messages</span>
            </h1>
          </div>
          <div className="am-stats">
            {["all", "unread", "read", "replied"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`am-filter-btn ${filter === s ? "am-filter-btn--active" : ""}`}
              >
                <span className="am-filter-count">{counts[s]}</span>
                <span className="am-filter-label">{s}</span>
              </button>
            ))}
          </div>
        </div>

        <hr className="am-hr" />

        {/* ── LIST ── */}
        {loading ? (
          <div className="am-empty">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="am-empty">No messages found.</div>
        ) : (
          <div className="am-list">
            {messages.map((msg) => (
              <div key={msg._id} className="am-card">

                {/* ── ROW ── */}
                <div
                  className="am-card-row"
                  onClick={() => handleExpand(msg)}
                >
                  <div className="am-card-left">
                    <span
                      className="am-status-dot"
                      style={{ background: STATUS_COLORS[msg.status].bg }}
                    />
                    <div className="am-card-meta">
                      <span className="am-card-name">{msg.name}</span>
                      <span className="am-card-email">{msg.email}</span>
                    </div>
                  </div>

                  <div className="am-card-center">
                    <span className="am-card-preview">
                      {msg.message.length > 80
                        ? msg.message.slice(0, 80) + "…"
                        : msg.message}
                    </span>
                  </div>

                  <div className="am-card-right">
                    <span className="am-card-date">
                      {new Date(msg.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </span>
                    <span
                      className="am-badge"
                      style={{
                        background: STATUS_COLORS[msg.status].bg,
                        color: STATUS_COLORS[msg.status].text,
                      }}
                    >
                      {msg.status}
                    </span>
                  </div>
                </div>

                {/* ── EXPANDED ── */}
                {expanded?._id === msg._id && (
                  <div className="am-expanded">
                    <hr className="am-hr am-hr--inner" />

                    <p className="am-full-message">{msg.message}</p>

                    {msg.adminReply && (
                      <div className="am-previous-reply">
                        <span className="am-reply-label">Previous reply</span>
                        <p className="am-reply-text">{msg.adminReply}</p>
                      </div>
                    )}

                    <div className="am-reply-area">
                      <textarea
                        className="am-reply-input"
                        rows={4}
                        placeholder={`Reply to ${msg.name}...`}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                      />
                      <div className="am-actions">
                        <button
                          className="am-btn am-btn--primary"
                          onClick={() => handleReply(msg._id)}
                          disabled={replyLoading || !replyText.trim()}
                        >
                          {replyLoading ? "Sending…" : "Send Reply"}
                        </button>
                        <button
                          className="am-btn am-btn--danger"
                          onClick={() => handleDelete(msg._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── TOAST ── */}
        {toast && <div className="am-toast">{toast}</div>}

      </div>
    </>
  );
}

const adminStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .am-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding-bottom: 80px;
  }

  /* HEADER */
  .am-header {
    max-width: 1200px;
    margin: 0 auto;
    padding: 64px 80px 40px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 24px;
  }
  .am-eyebrow {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 12px;
  }
  .am-eyebrow::before {
    content: '';
    width: 24px;
    height: 1px;
    background: black;
    display: block;
  }
  .am-title {
    font-size: clamp(28px, 4vw, 52px);
    font-weight: 200;
    color: #000;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1;
    margin: 0;
  }
  .am-title-bold { font-weight: 600; }

  /* FILTER BUTTONS */
  .am-stats {
    display: flex;
    gap: 2px;
  }
  .am-filter-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 18px;
    background: transparent;
    border: 1px solid rgba(0,0,0,0.12);
    cursor: pointer;
    font-family: 'Exo 2', sans-serif;
    transition: background 0.2s;
  }
  .am-filter-btn:hover,
  .am-filter-btn--active {
    background: #000;
    border-color: #000;
  }
  .am-filter-btn:hover .am-filter-count,
  .am-filter-btn:hover .am-filter-label,
  .am-filter-btn--active .am-filter-count,
  .am-filter-btn--active .am-filter-label {
    color: #fff;
  }
  .am-filter-count {
    font-size: 18px;
    font-weight: 300;
    color: #000;
    line-height: 1;
  }
  .am-filter-label {
    font-size: 7px;
    font-weight: 500;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: #000;
  }

  /* HR */
  .am-hr {
    max-width: 1200px;
    margin: 0 auto;
    border: none;
    border-top: 1px solid black;
    padding: 0 80px;
    box-sizing: content-box;
  }
  .am-hr--inner {
    max-width: 100%;
    padding: 0;
    margin: 0;
  }

  /* LIST */
  .am-list {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 80px;
  }
  .am-empty {
    max-width: 1200px;
    margin: 60px auto;
    padding: 0 80px;
    font-size: 11px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #aaa;
    text-align: center;
  }

  /* CARD */
  .am-card {
    border-bottom: 1px solid rgba(0,0,0,0.1);
  }
  .am-card-row {
    display: grid;
    grid-template-columns: 220px 1fr 160px;
    align-items: center;
    gap: 24px;
    padding: 20px 0;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .am-card-row:hover { opacity: 0.7; }

  .am-card-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .am-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .am-card-meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  .am-card-name {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    color: #000;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .am-card-email {
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.06em;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .am-card-center {
    min-width: 0;
  }
  .am-card-preview {
    font-size: 11px;
    font-weight: 300;
    letter-spacing: 0.04em;
    color: #444;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .am-card-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }
  .am-card-date {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.1em;
    color: #888;
  }
  .am-badge {
    font-size: 7px;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    padding: 3px 8px;
  }

  /* EXPANDED */
  .am-expanded {
    padding: 0 0 28px 18px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .am-full-message {
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.04em;
    color: #000;
    line-height: 1.9;
    margin: 0;
    white-space: pre-wrap;
  }
  .am-previous-reply {
    background: #fff;
    border-left: 2px solid #000;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .am-reply-label {
    font-size: 7px;
    font-weight: 500;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: #888;
  }
  .am-reply-text {
    font-size: 12px;
    font-weight: 300;
    color: #444;
    line-height: 1.7;
    margin: 0;
  }

  /* REPLY AREA */
  .am-reply-area {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .am-reply-input {
    background: #fff;
    border: 1px solid rgba(0,0,0,0.15);
    outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.04em;
    color: #000;
    padding: 14px;
    resize: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }
  .am-reply-input:focus { border-color: #000; }

  .am-actions {
    display: flex;
    gap: 8px;
  }
  .am-btn {
    font-family: 'Exo 2', sans-serif;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    padding: 12px 24px;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s;
  }
  .am-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .am-btn--primary { background: #000; color: #fff; }
  .am-btn--primary:hover:not(:disabled) { background: #222; }
  .am-btn--danger  { background: transparent; color: #000; border: 1px solid rgba(0,0,0,0.2); }
  .am-btn--danger:hover { background: #ff000010; border-color: red; color: red; }

  /* TOAST */
  .am-toast {
    position: fixed;
    bottom: 32px;
    right: 32px;
    background: #000;
    color: #fff;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    padding: 14px 22px;
    z-index: 9999;
  }

  /* RESPONSIVE */
  @media (max-width: 1024px) {
    .am-header { padding: 48px 40px 32px; }
    .am-hr     { padding: 0 40px; }
    .am-list, .am-empty { padding: 0 40px; }
  }
  @media (max-width: 768px) {
    .am-header { padding: 40px 20px 28px; }
    .am-hr     { padding: 0 20px; }
    .am-list, .am-empty { padding: 0 20px; }
    .am-card-row {
      grid-template-columns: 1fr;
      gap: 8px;
    }
    .am-card-right { align-items: flex-start; flex-direction: row; }
    .am-card-center { display: none; }
    .am-stats { flex-wrap: wrap; }
  }
`;
